import React, { useEffect, useLayoutEffect, useState, useCallback, useRef } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { IoChevronBack } from "react-icons/io5";

import styles from "../../../components/audio/homepage/PaginatedBookGrid.module.css";


import HomePageSkeleton from "../../../components/skeletonLoading/HomePageSkeleton";

// custom context
import { useNavbar } from "../../../context/NavBarContext";
// api call
import { useFetchAudioSeeAllDataQuery } from "../../../api/audioSeeAllPageApi";

import { NotFound } from "../../error/NotFound_404";

// Book Card Component
const BookCard = ({ item }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/audio/book-series/${item.item_id}`);
  };

  return (
    <div 
      className="flex flex-col cursor-pointer transform transition-transform hover:scale-105"
      onClick={handleClick}
    >
      <div className="relative rounded-lg overflow-hidden shadow-lg">
        <img
          src={item.image}
          alt={item.title}
          className="w-full h-48 object-cover"
          onError={(e) => {
            e.target.src = '/placeholder-book.jpg'; // fallback image
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
      </div>
      <div className="mt-2 px-1">
        <p className="text-sm font-medium text-gray-800 dark:text-gray-200 line-clamp-2 leading-tight">
          {item.title}
        </p>
      </div>
    </div>
  );
};

// Author Card Component
const AuthorCard = ({ item }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    console.log(item);
    navigate(`/audio/books-by-author/${item.item_id}`);
  };

  return (
    <div 
      className="flex flex-col items-center cursor-pointer transform transition-transform hover:scale-105"
      onClick={() => handleClick(item)}
    >
      <div className="relative mb-3">
        {/* Yellow circular background */}
        <div className="w-24 h-24 bg-yellow-400 rounded-full flex items-center justify-center p-1">
          <div className="w-full h-full rounded-full overflow-hidden bg-gray-200">
            <img
              src={item.image || item.author_image}
              alt={item.name || item.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = '/placeholder-author.jpg'; // fallback image
              }}
            />
          </div>
        </div>
      </div>
      <div className="text-center px-2">
        <p className="text-sm font-medium text-gray-800 dark:text-gray-200 line-clamp-2 leading-tight">
          {item.name || item.title}
        </p>
      </div>
    </div>
  );
};

// Collection Card Component
const CollectionCard = ({ item, onPress }) => {
  return (
    <div 
      className="cursor-pointer transform transition-all duration-300 hover:scale-[1.02] hover:shadow-xl"
      onClick={() => onPress(item)}
    >
      <div className={`relative rounded-2xl overflow-hidden shadow-lg h-32`}>
        {/* Background pattern overlay */}
        <div className="absolute inset-0 bg-black/10"></div>
        
        {/* Background image if available */}
        {item.image && (
          <img
            src={item.image}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        )}
        
        {/* Content */}
        <div className="relative h-full flex flex-col justify-between p-4">
          {/* Collection books preview (if available) */}
          {item.preview_books && item.preview_books.length > 0 && (
            <div className="flex space-x-2 mb-2">
              {item.preview_books.slice(0, 3).map((book, index) => (
                <div key={index} className="w-8 h-10 bg-white/20 rounded-sm overflow-hidden shadow-sm">
                  <img
                    src={book.image}
                    alt=""
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = '/placeholder-book-small.jpg';
                    }}
                  />
                </div>
              ))}
              {item.preview_books.length > 3 && (
                <div className="w-8 h-10 bg-white/20 rounded-sm flex items-center justify-center">
                  <span className="text-white text-xs font-bold">+{item.preview_books.length - 3}</span>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Decorative corner element */}
        <div className="absolute top-0 right-0 w-16 h-16 bg-white/10 rounded-bl-full"></div>
      </div>
    </div>
  );
};

// Loading Skeleton for Books Grid
const BookGridSkeleton = () => {
  return (
    <div className="grid grid-cols-2 gap-4 p-4">
      {[...Array(10)].map((_, index) => (
        <div key={index} className="animate-pulse">
          <div className="bg-gray-300 dark:bg-gray-700 rounded-lg h-48 w-full"></div>
          <div className="mt-2 space-y-2">
            <div className="bg-gray-300 dark:bg-gray-700 h-4 rounded w-3/4"></div>
            <div className="bg-gray-300 dark:bg-gray-700 h-4 rounded w-1/2"></div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Loading Skeleton for Authors Grid
const AuthorGridSkeleton = () => {
  return (
    <div className="grid grid-cols-3 gap-4 p-4">
      {[...Array(12)].map((_, index) => (
        <div key={index} className="animate-pulse flex flex-col items-center">
          <div className="w-24 h-24 bg-gray-300 dark:bg-gray-700 rounded-full mb-3"></div>
          <div className="space-y-2 w-full">
            <div className="bg-gray-300 dark:bg-gray-700 h-3 rounded w-3/4 mx-auto"></div>
            <div className="bg-gray-300 dark:bg-gray-700 h-3 rounded w-1/2 mx-auto"></div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Loading Skeleton for Collections Grid
const CollectionGridSkeleton = () => {
  return (
    <div className="grid grid-cols-1 gap-4 p-4">
      {[...Array(8)].map((_, index) => (
        <div key={index} className="animate-pulse">
          <div className="bg-gray-300 dark:bg-gray-700 rounded-2xl h-32 w-full"></div>
        </div>
      ))}
    </div>
  );
};

const Index = () => {
  const Navbar = useNavbar();
  
  const { category_id, category_type } = useParams();
  
  const [currentPage, setCurrentPage] = useState(1);
  const [allItems, setAllItems] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  // Refs to prevent duplicate calls
  const loadingRef = useRef(false);
  const lastScrollY = useRef(0);
  const mountedRef = useRef(true);
  
  // Include category_type in options if your API needs it
  const options = {
    category_id,
    category_type, // Add this if your API expects it
    page: currentPage
  };
  
  // fetch seeall api
  const { data, isLoading, error, isFetching } = useFetchAudioSeeAllDataQuery(options, {
    skip: !category_id, // Skip if no category_id
    refetchOnMountOrArgChange: true, // Force refetch when component mounts or args change
  });

  useLayoutEffect(() => {
    Navbar.setTogglePlace(<IoChevronBack fontSize="20" />);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Function to remove duplicates based on item_id
  const removeDuplicates = useCallback((items) => {
    const seen = new Set();
    return items.filter(item => {
      const id = item.item_id || item.author_id || item.collection_id || item.id;
      if (seen.has(id)) {
        return false;
      }
      seen.add(id);
      return true;
    });
  }, []);

  // Handle data updates
  useEffect(() => {
    if (!mountedRef.current) return;
    
    if (data?.result && Array.isArray(data.result)) {
      if (currentPage === 1) {
        // Reset items for first page and remove duplicates
        const uniqueItems = removeDuplicates(data.result);
        setAllItems(uniqueItems);
      } else {
        // Append items for subsequent pages and remove duplicates
        setAllItems(prev => {
          const combined = [...prev, ...data.result];
          return removeDuplicates(combined);
        });
      }
      
      // Check if there are more pages
      const hasMorePages = currentPage < (data.total_pages || 0);
      setHasMore(hasMorePages);
      setIsLoadingMore(false);
      loadingRef.current = false;
    }
  }, [data, currentPage, removeDuplicates]);

  // Reset when category changes - include both category_id and category_type
  useEffect(() => {
    console.log('Category changed:', { category_id, category_type });
    setCurrentPage(1);
    setAllItems([]);
    setHasMore(true);
    setIsLoadingMore(false);
    loadingRef.current = false;
    lastScrollY.current = 0;
  }, [category_id, category_type]); // Include both parameters

  // Throttled scroll handler to prevent multiple calls
  const handleScroll = useCallback(() => {
    if (!mountedRef.current) return;
    
    const currentScrollY = window.scrollY;
    
    // Only process if scrolling down
    if (currentScrollY <= lastScrollY.current) {
      lastScrollY.current = currentScrollY;
      return;
    }
    
    lastScrollY.current = currentScrollY;
    
    if (
      window.innerHeight + document.documentElement.scrollTop >=
      document.documentElement.offsetHeight - 1000 &&
      hasMore &&
      !isFetching &&
      !isLoadingMore &&
      !loadingRef.current
    ) {
      loadingRef.current = true;
      setIsLoadingMore(true);
      setCurrentPage(prev => prev + 1);
    }
  }, [hasMore, isFetching, isLoadingMore]);

  // Throttle scroll events
  useEffect(() => {
    let timeoutId = null;
    
    const throttledScroll = () => {
      if (timeoutId) return;
      
      timeoutId = setTimeout(() => {
        handleScroll();
        timeoutId = null;
      }, 200); // Throttle to 200ms
    };

    window.addEventListener('scroll', throttledScroll);
    return () => {
      window.removeEventListener('scroll', throttledScroll);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [handleScroll]);

  // Handle item press (book, author, or collection)
  const handleItemPress = (item) => {
    console.log('Item selected:', item);
    if (category_type === "author") {
      console.log('Author selected:', item);
      // Navigate to author detail page
      // navigate(`/author/${item.author_id || item.item_id}`);
    } else if (category_type === "collection") {
      console.log('Collection selected:', item);
      // Navigate to collection detail page
      // navigate(`/collection/${item.collection_id || item.item_id}`);
    } else {
      console.log('Book selected:', item);
      // Navigate to book detail page
      // navigate(`/book/${item.item_id}`);
    }
  };

  // Determine view type
  const isAuthorView = category_type === "author";
  const isCollectionView = category_type === "collection";

  // Early return for missing required params
  if (!category_id) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <p className="text-gray-600 dark:text-gray-400">Invalid category</p>
      </div>
    );
  }

  // Show loading skeleton when initially loading or when refetching first page
  if ((isLoading || isFetching) && currentPage === 1 && allItems.length === 0) {
    if (isAuthorView) return <AuthorGridSkeleton />;
    if (isCollectionView) return <CollectionGridSkeleton />;
    return <BookGridSkeleton />;
  }

  if (error) {
    console.error('API Error:', error);
    return <NotFound error={error} />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 relative">
      {/* Loading overlay when refetching data */}
      {isFetching && currentPage === 1 && allItems.length > 0 && (
        <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg flex flex-col items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
            <p className="text-gray-600 dark:text-gray-400 text-sm">Loading...</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 shadow-sm">
        <div className="px-4 py-3 flex items-center">
          {/* Loading indicator in header when fetching */}
          {isFetching && (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-500 mr-3"></div>
          )}
          <h5 className="text-md font-semibold text-yellow-500">
            {data?.category_name || (isFetching ? 'Loading...' : 'Category')}
          </h5>
        </div>
      </div>

      {/* Content Grid */}
      <div className="px-4 py-4">
        {/* Show loading skeleton if no data and currently fetching */}
        {!data && (isLoading || isFetching) && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading content...</p>
          </div>
        )}

        {/* Main content grid */}
        {allItems.length > 0 && (
          <div className={`grid gap-4 ${
            isCollectionView ? 'grid-cols-1' : 
            isAuthorView ? 'grid-cols-3' : 
            'grid-cols-2'
          }`}>
            {allItems.map((item, index) => {
              const key = item.item_id || item.author_id || item.collection_id || item.id || index;
              
              if (isCollectionView) {
                return (
                  <CollectionCard
                    key={key}
                    item={item}
                    onPress={handleItemPress}
                  />
                );
              } else if (isAuthorView) {
                return (
                  <AuthorCard
                    key={key}
                    item={item}
                    onPress={handleItemPress}
                  />
                );
              } else {
                return (
                  <BookCard
                    key={key}
                    item={item}
                    onPress={handleItemPress}
                  />
                );
              }
            })}
          </div>
        )}

        {/* Loading indicator for pagination */}
        {(isFetching || isLoadingMore) && currentPage > 1 && (
          // <div className="flex justify-center py-8">
          //   <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-lg flex items-center">
          //     <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
          //     <span className="text-gray-600 dark:text-gray-400 text-sm">Loading more...</span>
          //   </div>
          // </div>
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <div className={styles.spinner}></div>
          </div>
        )}

        {/* End of list indicator */}
        {!hasMore && allItems.length > 0 && (
          <div className="text-center py-8">
            <p className="text-gray-600 dark:text-gray-400">
              {isCollectionView ? "You've reached the end of collections" :
               isAuthorView ? "You've reached the end of authors" : 
               "You've reached the end of the list"}
            </p>
          </div>
        )}

        {/* No results */}
        {!isLoading && !isFetching && !isLoadingMore && allItems.length === 0 && (
          <div className="text-center py-16">
            <div className="mb-4">
              <svg className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <p className="text-gray-600 dark:text-gray-400 text-lg font-medium mb-2">
              {isCollectionView ? "No collections found" :
               isAuthorView ? "No authors found" : 
               "No books found"}
            </p>
            <p className="text-gray-500 dark:text-gray-500 text-sm">
              Try checking other categories or refreshing the page
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Index;